import React, { createContext, useContext, useState } from 'react';
import axios from 'axios';
import { useToast } from '@chakra-ui/react';

interface Location {
  lat: number;
  lng: number;
  address?: string;
}

interface Weather {
  temperature: number;
  description: string;
  icon: string;
  humidity: number;
  windSpeed: number;
}

interface LocationContextType {
  getCurrentLocation: () => Promise<Location>;
  getWeatherForLocation: (lat: number, lng: number) => Promise<Weather>;
  searchLocations: (query: string) => Promise<Location[]>;
  reverseGeocode: (lat: number, lng: number) => Promise<string>;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

export function LocationProvider({ children }: { children: React.ReactNode }) {
  const toast = useToast();
  const OPENWEATHER_API_KEY = process.env.REACT_APP_OPENWEATHER_API_KEY;
  const GEOCODING_API_KEY = process.env.REACT_APP_GEOCODING_API_KEY;

  const getCurrentLocation = (): Promise<Location> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          try {
            const address = await reverseGeocode(location.lat, location.lng);
            resolve({ ...location, address });
          } catch (error) {
            resolve(location);
          }
        },
        (error) => {
          reject(error);
          toast({
            title: 'Error getting location',
            description: error.message,
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
        }
      );
    });
  };

  const getWeatherForLocation = async (lat: number, lng: number): Promise<Weather> => {
    try {
      const response = await axios.get(
        `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lng}&appid=${OPENWEATHER_API_KEY}&units=metric`
      );

      return {
        temperature: response.data.main.temp,
        description: response.data.weather[0].description,
        icon: response.data.weather[0].icon,
        humidity: response.data.main.humidity,
        windSpeed: response.data.wind.speed,
      };
    } catch (error) {
      toast({
        title: 'Error fetching weather',
        description: 'Could not fetch weather data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      throw error;
    }
  };

  const searchLocations = async (query: string): Promise<Location[]> => {
    try {
      const response = await axios.get(
        `https://api.opencagedata.com/geocode/v1/json?q=${encodeURIComponent(query)}&key=${GEOCODING_API_KEY}`
      );

      return response.data.results.map((result: any) => ({
        lat: result.geometry.lat,
        lng: result.geometry.lng,
        address: result.formatted,
      }));
    } catch (error) {
      toast({
        title: 'Error searching locations',
        description: 'Could not search for locations',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      throw error;
    }
  };

  const reverseGeocode = async (lat: number, lng: number): Promise<string> => {
    try {
      const response = await axios.get(
        `https://api.opencagedata.com/geocode/v1/json?q=${lat}+${lng}&key=${GEOCODING_API_KEY}`
      );

      if (response.data.results.length > 0) {
        return response.data.results[0].formatted;
      }
      throw new Error('No results found');
    } catch (error) {
      toast({
        title: 'Error getting address',
        description: 'Could not get address for location',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      throw error;
    }
  };

  return (
    <LocationContext.Provider
      value={{
        getCurrentLocation,
        getWeatherForLocation,
        searchLocations,
        reverseGeocode,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
}

export function useLocation() {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
} 